import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@parrot-software/hooks';
import { usePaymentHistoryInfinite, useSubscriptionBlocks } from '@subscriptions/presentation/hooks';
import { EmptyState, ErrorState } from '@subscriptions/presentation/components/base';
import { buildOverdueChargeRows, getInvoicingDeadlineLabel } from '@subscriptions/presentation/utils';
import { PaymentHistoryMobileList } from './PaymentHistoryMobileList';
import { PaymentHistoryMobileListSkeleton } from './PaymentHistoryMobileListSkeleton';
import { PaymentHistoryTable } from './PaymentHistoryTable';
import { PaymentHistoryTableSkeleton } from './PaymentHistoryTableSkeleton';

/**
 * PaymentHistoryContent - Orchestrator for the "Historial de pagos" tab (organization level).
 *
 * Fetches paginated payment history with infinite scroll. Pagination cache is
 * cleared on unmount so that re-entering the tab always starts on page 1.
 *
 * The charges the organization owes go on top, oldest debt first. The subscriptions they come
 * from are already in cache from the sibling tab, so the tab rarely waits on them.
 */
export const PaymentHistoryContent = () => {
  const { t } = useTranslation('paymentHistory');
  const isMobile = useIsMobile();

  const {
    items,
    isLoading,
    isFetchingNextPage,
    isError,
    refetch,
    remove,
    hasNextPage,
    fetchNextPage,
  } = usePaymentHistoryInfinite();

  // The payments list never carries the unsettled charge: the API drops PENDING rows.
  const { data: blocks } = useSubscriptionBlocks();
  const rows = [...buildOverdueChargeRows(blocks, t('subscriptionChargeConcept')), ...items];
  const hasRows = rows.length > 0;
  // The owed charges are ours, so they must not vouch for a history that failed to load.
  const hasFailed = isError && items.length === 0;

  useEffect(() => {
    return () => remove();
  }, [remove]);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold">{t('title')}</h2>
        <p className="text-sm text-muted-foreground">{t('subtitle', { deadline: getInvoicingDeadlineLabel() })}</p>
      </div>

      {isLoading && (isMobile ? <PaymentHistoryMobileListSkeleton /> : <PaymentHistoryTableSkeleton />)}

      {hasFailed && (
        <ErrorState
          description={t('errorDescription')}
          onRetry={() => refetch()}
          retryLabel={t('retry')}
          title={t('errorLoading')}
        />
      )}

      {!isLoading && !hasFailed && !hasRows && (
        <EmptyState description={t('emptyDescription')} title={t('emptyTitle')} />
      )}

      {!isLoading &&
        !hasFailed &&
        hasRows &&
        (isMobile ? (
          <PaymentHistoryMobileList
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
            payments={rows}
          />
        ) : (
          <PaymentHistoryTable
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
            payments={rows}
          />
        ))}
    </div>
  );
};
